import type { Campaign, EmailContent } from './types';
import { MOCK_BRAND_ID } from './mockReference';

const DAY_MS = 86_400_000;

const email = (subject: string, headline: string, body: string, overrides: Partial<EmailContent> = {}): EmailContent => ({
  subject, preheader: '', templateId: 'promo_basic', headline, body, imageUrl: '', ctaLabel: 'Order now', ctaTarget: 'online_ordering', ...overrides,
});

/** One-time campaigns covering every status. Dates are relative to `now` so the list always looks current. */
export function buildMockCampaigns(now: Date = new Date()): Campaign[] {
  const at = (daysFromNow: number, hour = 17) => {
    const d = new Date(now.getTime() + daysFromNow * DAY_MS);
    d.setUTCHours(hour, 0, 0, 0);
    return d.toISOString();
  };
  const base = (id: string, name: string, createdDaysAgo: number, overrides: Partial<Campaign>): Campaign => ({
    id,
    brandId: MOCK_BRAND_ID,
    createdAt: at(-createdDaysAgo, 15),
    updatedAt: at(-createdDaysAgo, 15),
    name,
    kind: 'one_time',
    status: 'draft',
    segmentId: null,
    triggerType: null,
    triggerConfig: {},
    outletIds: [],
    channels: ['sms'],
    channelMode: 'first_available',
    content: {},
    rewardType: 'none',
    rewardConfig: {},
    scheduledAt: null,
    attributionWindowDays: 7,
    holdoutPercent: 0,
    createdBy: 'user_owner',
    approvedBy: null,
    lastSentAt: null,
    audienceCount: 0,
    attributedRevenueCents: 0,
    sentLast30Days: 0,
    revenueLast30DaysCents: 0,
    spendCents: 0,
    pausedFrom: null,
    ...overrides,
  });

  return [
    base('cmp_weekend_dosa', 'Weekend dosa festival', 24, {
      status: 'completed', segmentId: 'seg_dosa_fans', channels: ['sms', 'email'], lastSentAt: at(-21), approvedBy: 'user_owner',
      content: {
        sms: { body: 'Hi {{first_name}}, dosa festival this weekend! 15% off any dosa with code {{reward_code}}. {{link}}' },
        email: email('Dosa festival this weekend', 'Three days of dosas', 'Hi {{first_name}}, join us this weekend for our dosa festival. Use code {{reward_code}} for 15% off, valid until {{reward_expiry}}.'),
      },
      rewardType: 'offer',
      rewardConfig: { offerId: 'mock_offer_dosa15', offerName: '15% off dosas', delivery: 'unique_code', validDays: 14, maxRedemptionsPerCustomer: 1 },
    }),
    base('cmp_app_launch', 'Try our new app: 15% off', 8, {
      status: 'completed', segmentId: 'seg_not_on_app', channels: ['sms', 'email'], lastSentAt: at(-6), approvedBy: 'user_owner',
      content: {
        sms: { body: 'Hi {{first_name}}, our new app is here. Get 15% off your first app order with code {{reward_code}}. {{app_link}}' },
        email: email('Our new app is here', '15% off your first app order', 'Hi {{first_name}}, order ahead and skip the queue with the {{brand_name}} app. Use code {{reward_code}} for 15% off, valid until {{reward_expiry}}.', { ctaLabel: 'Get the app', ctaTarget: 'get_app' }),
      },
      rewardType: 'offer',
      rewardConfig: { offerId: 'mock_offer_app15', offerName: '15% off first app order', delivery: 'unique_code', redeemIn: 'app_only', validDays: 14, maxRedemptionsPerCustomer: 1 },
    }),
    base('cmp_at_risk_winback', 'We miss you: at-risk customers', 16, {
      status: 'completed', segmentId: 'seg_at_risk', channels: ['sms', 'email', 'push'], lastSentAt: at(-14), approvedBy: 'user_owner',
      content: {
        sms: { body: 'We miss you, {{first_name}}! Come back this week and enjoy a treat on us. {{link}}' },
        email: email('We miss you', 'It has been a while', 'Hi {{first_name}}, we have not seen you in a while. Come back this week.'),
        push: { title: 'We miss you', body: 'Come back this week for something special.', deepLink: 'offers' },
      },
    }),
    base('cmp_members_double', 'Double points week', 11, {
      status: 'completed', segmentId: 'seg_members', channels: ['email', 'push'], channelMode: 'all_selected', lastSentAt: at(-9), approvedBy: 'user_owner',
      content: {
        email: email('Double points all week', 'Earn 2x points', 'Hi {{first_name}}, earn double points on every order until {{reward_expiry}}. Your balance: {{points_balance}}.', { templateId: 'reward', ctaTarget: 'loyalty', ctaLabel: 'View my points' }),
        push: { title: 'Double points this week', body: 'Every order earns 2x points until Sunday.', deepLink: 'loyalty' },
      },
      rewardType: 'points_multiplier', rewardConfig: { multiplier: 2, validDays: 7 },
    }),
    base('cmp_new_menu', 'New autumn menu', 7, {
      status: 'completed', segmentId: 'seg_reachable', channels: ['email'], lastSentAt: at(-5), approvedBy: 'user_owner',
      content: { email: email('Our autumn menu is here', 'New season, new flavours', 'Hi {{first_name}}, our autumn menu has arrived at {{brand_name}}. Come and try it.', { templateId: 'announcement', ctaTarget: 'menu', ctaLabel: 'See the menu' }) },
    }),
    base('cmp_lunch_rush', 'Lunch-time members special', 2, {
      status: 'sending', segmentId: 'seg_lunch_members', channels: ['sms', 'push'], lastSentAt: new Date(now.getTime() - 4 * 60_000).toISOString(), approvedBy: 'user_owner',
      content: {
        sms: { body: '{{first_name}}, lunch is on: 100 bonus points when you order today. {{link}}' },
        push: { title: 'Lunch bonus today', body: '100 bonus points on your lunch order.', deepLink: 'online_ordering' },
      },
      rewardType: 'bonus_points', rewardConfig: { points: 100 },
    }),
    base('cmp_regulars_thanks', 'Thank you, regulars', 3, {
      status: 'paused', pausedFrom: 'sending', segmentId: 'seg_regulars', channels: ['sms'], lastSentAt: at(-1), approvedBy: 'user_owner',
      content: { sms: { body: 'Thank you for being a regular, {{first_name}}! A free filter coffee is waiting for you. {{link}}' } },
      rewardType: 'free_reward', rewardConfig: { loyaltyRewardId: 'mock_reward_coffee', loyaltyRewardName: 'Free filter coffee', validDays: 14 },
    }),
    base('cmp_diwali', 'Diwali sweets pre-order', 4, {
      status: 'scheduled', segmentId: 'seg_reachable', channels: ['email', 'sms'], scheduledAt: at(6, 18), approvedBy: 'user_owner',
      content: {
        email: email('Pre-order your Diwali sweets', 'Diwali is coming', 'Hi {{first_name}}, pre-order sweet boxes from {{brand_name}} for Diwali.', { templateId: 'announcement' }),
        sms: { body: 'Diwali sweet boxes are open for pre-order, {{first_name}}. {{link}}' },
      },
    }),
    base('cmp_birthday_month', 'Birthday month treat', 2, {
      status: 'scheduled', segmentId: 'seg_birthday', channels: ['sms', 'email'], scheduledAt: at(2, 19), approvedBy: 'user_owner',
      content: {
        sms: { body: 'Happy birthday month, {{first_name}}! Dessert is on us. {{link}}' },
        email: email('A birthday treat for you', 'Happy birthday month', 'Hi {{first_name}}, enjoy a dessert on us this month at {{outlet_name}}.', { templateId: 'reward' }),
      },
    }),
    base('cmp_high_spender', 'VIP tasting evening', 1, {
      status: 'draft', segmentId: 'seg_high_spenders', channels: ['email'],
      content: { email: email('You are invited', 'A tasting evening for our best guests', 'Hi {{first_name}}, join us for a private tasting evening.', { templateId: 'announcement', ctaTarget: 'reservation', ctaLabel: 'Reserve a seat' }) },
    }),
    base('cmp_untitled', 'Monsoon combo offer', 0, {
      status: 'draft', channels: ['sms'], content: { sms: { body: '' } },
    }),
    base('cmp_enroll', 'Join rewards: 200 bonus points', 34, {
      status: 'cancelled', segmentId: 'seg_non_members', channels: ['sms', 'email'], approvedBy: 'user_owner',
      content: {
        sms: { body: '{{first_name}}, join our rewards program and get 200 bonus points. {{link}}' },
        email: email('Join rewards and get 200 points', 'Earn on every order', 'Hi {{first_name}}, join the {{brand_name}} rewards program today and start with 200 bonus points.', { templateId: 'reward', ctaTarget: 'loyalty', ctaLabel: 'Join now' }),
      },
      rewardType: 'bonus_points', rewardConfig: { points: 200 },
    }),
    base('cmp_summer', 'Summer mango lassi launch', 96, {
      status: 'archived', segmentId: 'seg_reachable', channels: ['email'], lastSentAt: at(-92), approvedBy: 'user_owner',
      content: { email: email('Mango lassi is back', 'Summer in a glass', 'Hi {{first_name}}, our mango lassi is back for the summer.', { ctaTarget: 'menu', ctaLabel: 'See the menu' }) },
    }),
  ];
}
